import { FormEvent, useCallback, useEffect, useState } from 'react';
import { getAdminSession } from '../adminSession';
import AdminContractDetail from './AdminContractDetail';
import AdminContractForm from './AdminContractForm';
import AdminContractList from './AdminContractList';
import {
  type CreateContractPayload,
  type OnlineContract,
  createContract,
  deleteContract,
  fetchContractDetail,
  fetchContractList,
  updateContract,
} from './adminContractApi';
import {
  type StatusFilter,
  DEFAULT_TERMS_TEXT,
  createEmptyForm,
} from './adminContractUtils';

type ViewMode = 'list' | 'create' | 'edit' | 'detail';

export default function AdminContractManage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [contracts, setContracts] = useState<OnlineContract[]>([]);
  const [detailContract, setDetailContract] = useState<OnlineContract | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [form, setForm] = useState<CreateContractPayload>(createEmptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadContracts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchContractList({
        q: appliedSearch.trim() || undefined,
        status: statusFilter,
      });
      setContracts(list);
    } catch (err) {
      setContracts([]);
      setError(err instanceof Error ? err.message : '계약 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, statusFilter]);

  useEffect(() => {
    if (viewMode !== 'list') return;
    void loadContracts();
  }, [viewMode, loadContracts]);

  const resetForm = () => {
    setForm(createEmptyForm());
    setEditingId(null);
  };

  const goToList = () => {
    resetForm();
    setDetailContract(null);
    setViewMode('list');
  };

  const openCreatePage = () => {
    resetForm();
    setDetailContract(null);
    setViewMode('create');
  };

  const openDetailPage = async (row: OnlineContract) => {
    setDetailLoading(true);
    setError(null);
    setViewMode('detail');
    try {
      const detail = await fetchContractDetail(row.id);
      setDetailContract(detail);
    } catch (err) {
      setDetailContract(null);
      setError(err instanceof Error ? err.message : '계약 상세를 불러오지 못했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditPage = (row: OnlineContract) => {
    if (row.status === 'signed') {
      alert('이미 서명된 계약은 수정할 수 없습니다.');
      return;
    }

    setDetailContract(null);
    setEditingId(row.id);
    setForm({
      customer_name: row.customer_name || '',
      phone: row.phone || '',
      contract_type: row.contract_type || 'web',
      contract_name: row.contract_name || '',
      product_name: row.product_name || '',
      company_name: row.company_name || '',
      representative: row.representative || '',
      business_number: row.business_number || '',
      email: row.email || '',
      address: row.address || '',
      build_cost: row.build_cost || '',
      build_period: row.build_period || '',
      maintenance_fee: row.maintenance_fee || '',
      maintenance_period: row.maintenance_period || '',
      payment_terms: row.payment_terms?.length
        ? row.payment_terms
        : [{ category: '', amount: '', condition: '' }],
      special_notes: row.special_notes || '',
      terms_text: row.terms_text || DEFAULT_TERMS_TEXT,
    });
    setViewMode('edit');
  };

  const updateField = <K extends keyof CreateContractPayload>(
    key: K,
    value: CreateContractPayload[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setAppliedSearch(searchQuery.trim());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const customerName = String(form.customer_name || '').trim();
    const phone = String(form.phone || '').trim();

    if (!customerName) {
      alert('고객명을 입력해 주세요.');
      return;
    }
    if (!phone) {
      alert('전화번호를 입력해 주세요.');
      return;
    }

    const session = getAdminSession();
    const payload: CreateContractPayload = {
      customer_name: customerName,
      phone,
      contract_type: form.contract_type,
      contract_name: String(form.contract_name || '').trim(),
      product_name: String(form.product_name || '').trim(),
      company_name: String(form.company_name || '').trim(),
      representative: String(form.representative || '').trim() || customerName,
      business_number: String(form.business_number || '').trim(),
      email: String(form.email || '').trim(),
      address: String(form.address || '').trim(),
      build_cost: String(form.build_cost || '').trim(),
      build_period: String(form.build_period || '').trim(),
      maintenance_fee: String(form.maintenance_fee || '').trim(),
      maintenance_period: String(form.maintenance_period || '').trim(),
      payment_terms: (form.payment_terms || []).filter(
        (row) => row.category.trim() || row.amount.trim() || row.condition.trim(),
      ),
      special_notes: String(form.special_notes || '').trim(),
      terms_text: String(form.terms_text || '').trim() || DEFAULT_TERMS_TEXT,
      created_by_name: session?.name ?? '',
      created_by_email: session?.email ?? sessionStorage.getItem('user') ?? '',
    };

    setSubmitting(true);
    setError(null);
    try {
      if (viewMode === 'edit' && editingId != null) {
        await updateContract(editingId, payload);
        alert('계약이 수정되었습니다.');
      } else {
        await createContract(payload);
        alert('계약이 등록되었습니다. 고객은 고객명과 전화번호로 조회할 수 있습니다.');
      }
      goToList();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : viewMode === 'edit'
            ? '계약 수정에 실패했습니다.'
            : '계약 등록에 실패했습니다.';
      setError(message);
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row: OnlineContract) => {
    const ok = window.confirm(
      `「${row.customer_name} / ${row.contract_name || '계약'}」 계약을 삭제하시겠습니까?`,
    );
    if (!ok) return;

    setDeletingId(row.id);
    setError(null);
    try {
      await deleteContract(row.id);
      if (editingId === row.id || detailContract?.id === row.id) {
        goToList();
        return;
      }
      await loadContracts();
    } catch (err) {
      const message = err instanceof Error ? err.message : '계약 삭제에 실패했습니다.';
      setError(message);
      alert(message);
    } finally {
      setDeletingId(null);
    }
  };

  if (viewMode === 'detail') {
    return (
      <AdminContractDetail
        contract={detailContract}
        loading={detailLoading}
        error={error}
        deletingId={deletingId}
        onBack={goToList}
        onEdit={openEditPage}
        onDelete={(row) => void handleDelete(row)}
      />
    );
  }

  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <AdminContractForm
        mode={viewMode}
        form={form}
        error={error}
        submitting={submitting}
        onBack={goToList}
        onFieldChange={updateField}
        onSubmit={(event) => void handleSubmit(event)}
      />
    );
  }

  return (
    <AdminContractList
      contracts={contracts}
      loading={loading}
      error={error}
      deletingId={deletingId}
      searchQuery={searchQuery}
      statusFilter={statusFilter}
      onSearchQueryChange={setSearchQuery}
      onStatusFilterChange={setStatusFilter}
      onSearch={handleSearch}
      onRefresh={() => void loadContracts()}
      onCreate={openCreatePage}
      onDetail={(row) => void openDetailPage(row)}
      onEdit={openEditPage}
      onDelete={(row) => void handleDelete(row)}
    />
  );
}
